import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../api';
import { toast } from 'sonner';
import {
    Search, Filter, CheckCircle, AlertTriangle, XCircle,
    Star, Globe, Mail, Trash2, ArrowRight, X, ChevronDown,
    Zap, FileText, Activity, Tag, ChevronLeft, ChevronRight,
    ArrowUp, ArrowDown, ArrowUpDown, Phone, Calendar
} from 'lucide-react';

const ROLE_OPTIONS = ['Owner', 'Ops', 'Estimator', 'Finance', 'Other'];
const SYSTEM_OPTIONS = ['Excel', 'Sage', 'Xero', 'QuickBooks', 'Bespoke', 'Other'];
const OUTCOME_OPTIONS = ['No answer', 'Reception', 'Sent email', 'Demo booked', 'Not interested'];
const YES_NO_UNKNOWN = [
    { v: 'yes', label: 'Yes' },
    { v: 'no', label: 'No' },
    { v: 'unknown', label: 'Unknown' },
];
const YES_NO_LATER = [
    { v: 'yes', label: 'Yes' },
    { v: 'no', label: 'No' },
    { v: 'later', label: 'Later' },
];

const PAGE_SIZE = 20;

const STAGES = [
    { key: 'new_leads', label: 'New Leads', color: '#002FA7' },
    { key: 'verified', label: 'Verified', color: '#6366F1' },
    { key: 'contacted', label: 'Contacted', color: '#F59E0B' },
    { key: 'engaged', label: 'Engaged', color: '#10B981' },
    { key: 'qualified', label: 'Qualified', color: '#00A86B' },
    { key: 'won', label: 'Won', color: '#059669' },
    { key: 'lost', label: 'Lost', color: '#FF3333' },
];

const STAGE_MAP = Object.fromEntries(STAGES.map(s => [s.key, s]));

function EmailStatusBadge({ status }) {
    const configs = {
        valid: { label: 'Valid', bg: '#F0FDF4', text: '#16A34A', border: '#BBF7D0' },
        risky: { label: 'Risky', bg: '#FFFBEB', text: '#D97706', border: '#FDE68A' },
        invalid: { label: 'Invalid', bg: '#FFF1F2', text: '#BE123C', border: '#FECDD3' },
        not_verified: { label: 'Unverified', bg: '#F4F4F5', text: '#71717A', border: '#E4E4E7' },
        no_email: { label: 'No Email', bg: '#F4F4F5', text: '#A1A1AA', border: '#E4E4E7' },
    };
    const c = configs[status] || configs.not_verified;
    return (
        <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 border rounded-none"
            style={{ background: c.bg, color: c.text, borderColor: c.border }}>
            {c.label}
        </span>
    );
}

function StageBadge({ stage }) {
    const s = STAGE_MAP[stage] || { label: stage, color: '#71717A' };
    return (
        <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 border rounded-none"
            style={{ background: `${s.color}15`, color: s.color, borderColor: `${s.color}40` }}>
            {s.label}
        </span>
    );
}

function ScoreBadge({ score }) {
    const color = score >= 70 ? '#00A86B' : score >= 40 ? '#F59E0B' : '#FF3333';
    return (
        <span className="mono text-xs font-bold px-1.5 py-0.5 border" style={{ color, borderColor: color, background: `${color}15` }}>
            {score}
        </span>
    );
}

function TriggerBadge({ trigger }) {
    const colors = {
        no_website: { bg: '#FFF7ED', text: '#C2410C', border: '#FED7AA' },
        low_rating: { bg: '#FFF1F2', text: '#BE123C', border: '#FECDD3' },
        low_reviews: { bg: '#FFFBEB', text: '#92400E', border: '#FDE68A' },
    };
    const c = colors[trigger.type] || { bg: '#F0F9FF', text: '#0369A1', border: '#BAE6FD' };
    return (
        <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 border rounded-none"
            style={{ background: c.bg, color: c.text, borderColor: c.border }}>
            {trigger.label}
        </span>
    );
}

/**
 * Clickable column header. Cycles asc → desc on repeated clicks; switches
 * field and starts asc when a new column is selected. The component is
 * intentionally generic so it can be reused on any table in the app.
 */
function SortableTh({ field, sortBy, sortDir, onSort, align = 'left', children, testId }) {
    const active = sortBy === field;
    const Icon = !active ? ArrowUpDown : (sortDir === 'asc' ? ArrowUp : ArrowDown);
    const justify = align === 'center' ? 'justify-center' : align === 'right' ? 'justify-end' : 'justify-start';
    return (
        <th
            className={`text-${align} cursor-pointer select-none hover:bg-neutral-100 transition-colors group`}
            onClick={() => onSort(field)}
            data-testid={testId || `sort-${field}`}
            aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
        >
            <span className={`inline-flex items-center gap-1.5 ${justify} w-full`}>
                <span>{children}</span>
                <Icon
                    size={11}
                    className={active ? 'text-[#002FA7]' : 'text-neutral-300 group-hover:text-neutral-500'}
                    strokeWidth={2.5}
                />
            </span>
        </th>
    );
}

function DiscoveryCallSection({ lead, onUpdate }) {
    const initial = lead?.discovery || {};
    const [form, setForm] = useState({
        contact_name: initial.contact_name || '',
        role: initial.role || '',
        current_systems: Array.isArray(initial.current_systems) ? initial.current_systems : [],
        uses_slab_layouts: initial.uses_slab_layouts || '',
        pain_points: initial.pain_points || '',
        demo_interest: initial.demo_interest || '',
        contact_email: initial.contact_email || '',
        follow_up_date: initial.follow_up_date || '',
        call_outcome: initial.call_outcome || '',
        additional_notes: initial.additional_notes || '',
    });
    const [saving, setSaving] = useState(false);
    const [open, setOpen] = useState(false);

    const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
    const toggleSystem = (sys) => {
        setForm(p => ({
            ...p,
            current_systems: p.current_systems.includes(sys)
                ? p.current_systems.filter(s => s !== sys)
                : [...p.current_systems, sys],
        }));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const { data } = await api.put(`/leadgen/leads/${lead.id}/discovery`, form);
            onUpdate(data);
            toast.success('Discovery call saved');
            setOpen(false);
        } catch {
            toast.error('Failed to save discovery call');
        } finally {
            setSaving(false);
        }
    };

    // Compact summary text shown when collapsed
    const summaryBits = [
        initial.contact_name && `${initial.contact_name}${initial.role ? ` (${initial.role})` : ''}`,
        initial.call_outcome,
        initial.follow_up_date && `Follow-up ${initial.follow_up_date}`,
    ].filter(Boolean);

    return (
        <div className="border border-[#002FA7]/25 bg-[#002FA7]/[0.03]" data-testid="discovery-section">
            <button
                onClick={() => setOpen(p => !p)}
                className="w-full flex items-center justify-between px-4 py-3 text-left"
                data-testid="discovery-toggle"
            >
                <div className="flex items-center gap-2 min-w-0">
                    <Phone size={12} className="text-[#002FA7] flex-shrink-0" />
                    <span className="text-xs font-bold uppercase tracking-widest text-[#002FA7]">Discovery Call</span>
                    {summaryBits.length > 0 && (
                        <span className="text-[11px] text-neutral-500 truncate">— {summaryBits.join(' · ')}</span>
                    )}
                </div>
                <ChevronDown size={13} className={`text-[#002FA7] transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>

            {open && (
                <div className="px-4 pb-4 pt-1 space-y-4 border-t border-[#002FA7]/10">
                    {/* Contact Name + Role */}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-[10px] font-semibold uppercase tracking-widest text-neutral-500 mb-1">Contact Name</label>
                            <input
                                type="text"
                                value={form.contact_name}
                                onChange={e => set('contact_name', e.target.value)}
                                className="w-full border border-neutral-300 bg-white px-2.5 py-1.5 text-xs rounded-none focus:outline-none focus:border-[#002FA7]"
                                data-testid="discovery-contact-name"
                            />
                        </div>
                        <div>
                            <label className="block text-[10px] font-semibold uppercase tracking-widest text-neutral-500 mb-1">Role</label>
                            <select
                                value={form.role}
                                onChange={e => set('role', e.target.value)}
                                className="w-full border border-neutral-300 bg-white px-2 py-1.5 text-xs rounded-none focus:outline-none focus:border-[#002FA7]"
                                data-testid="discovery-role"
                            >
                                <option value="">—</option>
                                {ROLE_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
                            </select>
                        </div>
                    </div>

                    {/* Current Systems — multi-select chips */}
                    <div>
                        <label className="block text-[10px] font-semibold uppercase tracking-widest text-neutral-500 mb-1.5">Current Systems</label>
                        <div className="flex flex-wrap gap-1.5">
                            {SYSTEM_OPTIONS.map(sys => {
                                const active = form.current_systems.includes(sys);
                                return (
                                    <button
                                        key={sys}
                                        type="button"
                                        onClick={() => toggleSystem(sys)}
                                        className={`text-[11px] px-2.5 py-1 border transition-colors ${active
                                            ? 'bg-[#002FA7] border-[#002FA7] text-white font-semibold'
                                            : 'bg-white border-neutral-300 text-neutral-600 hover:border-[#002FA7]'}`}
                                        data-testid={`discovery-system-${sys.toLowerCase()}`}
                                    >
                                        {sys}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Uses Slab Layouts */}
                    <div>
                        <label className="block text-[10px] font-semibold uppercase tracking-widest text-neutral-500 mb-1.5">Uses Slab Layouts?</label>
                        <div className="flex gap-1.5">
                            {YES_NO_UNKNOWN.map(o => (
                                <button
                                    key={o.v}
                                    type="button"
                                    onClick={() => set('uses_slab_layouts', o.v)}
                                    className={`text-[11px] px-3 py-1 border transition-colors flex-1 ${form.uses_slab_layouts === o.v
                                        ? 'bg-[#002FA7] border-[#002FA7] text-white font-semibold'
                                        : 'bg-white border-neutral-300 text-neutral-600 hover:border-[#002FA7]'}`}
                                    data-testid={`discovery-slab-${o.v}`}
                                >
                                    {o.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Pain Points */}
                    <div>
                        <label className="block text-[10px] font-semibold uppercase tracking-widest text-neutral-500 mb-1">Pain Points</label>
                        <textarea
                            value={form.pain_points}
                            onChange={e => set('pain_points', e.target.value)}
                            rows={2}
                            placeholder="e.g. manual quoting, no slab tracking, finance reconciliation pain..."
                            className="w-full border border-neutral-300 bg-white px-2.5 py-1.5 text-xs rounded-none focus:outline-none focus:border-[#002FA7] resize-none leading-relaxed"
                            data-testid="discovery-pain-points"
                        />
                    </div>

                    {/* Demo Interest */}
                    <div>
                        <label className="block text-[10px] font-semibold uppercase tracking-widest text-neutral-500 mb-1.5">Demo Interest</label>
                        <div className="flex gap-1.5">
                            {YES_NO_LATER.map(o => (
                                <button
                                    key={o.v}
                                    type="button"
                                    onClick={() => set('demo_interest', o.v)}
                                    className={`text-[11px] px-3 py-1 border transition-colors flex-1 ${form.demo_interest === o.v
                                        ? 'bg-[#002FA7] border-[#002FA7] text-white font-semibold'
                                        : 'bg-white border-neutral-300 text-neutral-600 hover:border-[#002FA7]'}`}
                                    data-testid={`discovery-demo-${o.v}`}
                                >
                                    {o.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Contact Email + Follow-up Date */}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-[10px] font-semibold uppercase tracking-widest text-neutral-500 mb-1">Email (contact)</label>
                            <input
                                type="email"
                                value={form.contact_email}
                                onChange={e => set('contact_email', e.target.value)}
                                placeholder="person@company.co.uk"
                                className="w-full border border-neutral-300 bg-white px-2.5 py-1.5 text-xs rounded-none focus:outline-none focus:border-[#002FA7]"
                                data-testid="discovery-contact-email"
                            />
                        </div>
                        <div>
                            <label className="block text-[10px] font-semibold uppercase tracking-widest text-neutral-500 mb-1">Follow-up Date</label>
                            <input
                                type="date"
                                value={form.follow_up_date}
                                onChange={e => set('follow_up_date', e.target.value)}
                                className="w-full border border-neutral-300 bg-white px-2 py-1.5 text-xs rounded-none focus:outline-none focus:border-[#002FA7]"
                                data-testid="discovery-followup-date"
                            />
                        </div>
                    </div>

                    {/* Call Outcome */}
                    <div>
                        <label className="block text-[10px] font-semibold uppercase tracking-widest text-neutral-500 mb-1">Call Outcome</label>
                        <select
                            value={form.call_outcome}
                            onChange={e => set('call_outcome', e.target.value)}
                            className="w-full border border-neutral-300 bg-white px-2 py-1.5 text-xs rounded-none focus:outline-none focus:border-[#002FA7]"
                            data-testid="discovery-outcome"
                        >
                            <option value="">—</option>
                            {OUTCOME_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                        </select>
                    </div>

                    {/* Additional Notes */}
                    <div>
                        <label className="block text-[10px] font-semibold uppercase tracking-widest text-neutral-500 mb-1">Additional Notes</label>
                        <textarea
                            value={form.additional_notes}
                            onChange={e => set('additional_notes', e.target.value)}
                            rows={3}
                            className="w-full border border-neutral-300 bg-white px-2.5 py-1.5 text-xs rounded-none focus:outline-none focus:border-[#002FA7] resize-none leading-relaxed"
                            data-testid="discovery-additional-notes"
                        />
                    </div>

                    {/* Save */}
                    <div className="flex items-center justify-between pt-2 border-t border-[#002FA7]/10">
                        <span className="text-[10px] text-neutral-400">
                            {initial.updated_at ? `Last saved ${initial.updated_at.slice(0, 10)} by ${initial.updated_by || '—'}` : 'Not yet saved'}
                        </span>
                        <button
                            type="button"
                            onClick={handleSave}
                            disabled={saving}
                            className="bg-[#002FA7] hover:bg-[#00227A] text-white text-xs font-semibold uppercase tracking-wider px-4 py-1.5 transition-colors disabled:opacity-50"
                            data-testid="discovery-save-btn"
                        >
                            {saving ? 'Saving...' : 'Save Discovery'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

function LeadDetailsEditor({ lead, onUpdate }) {
    const toForm = (l) => ({
        name: l.name || '', address: l.address || '', website: l.website || '',
        email: l.email || '', phone: l.phone || '', category: l.category || '', location: l.location || '',
        company_email: l.company_email || '', company_phone: l.company_phone || '', company_linkedin: l.company_linkedin || '',
        description: l.description || '', solution_fit: l.solution_fit || '', distance_mi: l.distance_mi ?? '',
        verification_notes: l.verification_notes || '',
        contacts: (l.contacts || []).map(c => ({ ...c })),
        sources: [...(l.sources || [])],
    });
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState(() => toForm(lead));

    useEffect(() => { setForm(toForm(lead)); }, [lead.id]);

    const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
    const setContact = (i, k, v) => setForm(p => ({ ...p, contacts: p.contacts.map((c, idx) => idx === i ? { ...c, [k]: v } : c) }));
    const addContact = () => setForm(p => ({ ...p, contacts: [...p.contacts, { name: '', title: '', email: '', phone: '', linkedin: '' }] }));
    const removeContact = (i) => setForm(p => ({ ...p, contacts: p.contacts.filter((_, idx) => idx !== i) }));
    const setSource = (i, v) => setForm(p => ({ ...p, sources: p.sources.map((s, idx) => idx === i ? v : s) }));
    const addSource = () => setForm(p => ({ ...p, sources: [...p.sources, ''] }));
    const removeSource = (i) => setForm(p => ({ ...p, sources: p.sources.filter((_, idx) => idx !== i) }));

    const handleSave = async () => {
        setSaving(true);
        try {
            const payload = {
                ...form,
                distance_mi: form.distance_mi === '' ? null : Number(form.distance_mi),
                contacts: form.contacts.filter(c => c.name.trim()),
                sources: form.sources.filter(s => s.trim()),
            };
            const { data } = await api.put(`/leadgen/leads/${lead.id}`, payload);
            onUpdate(data);
            toast.success('Lead details saved');
            setEditing(false);
        } catch { toast.error('Failed to save lead details'); } finally { setSaving(false); }
    };

    const fieldCls = "w-full border border-neutral-300 bg-white px-2.5 py-1.5 text-xs rounded-none focus:outline-none focus:border-[#002FA7]";
    const labelCls = "block text-[10px] font-semibold uppercase tracking-widest text-neutral-500 mb-1";

    if (!editing) {
        return (
            <div className="border border-neutral-100 p-4 space-y-2.5" data-testid="lead-details-view">
                <div className="flex items-center justify-between mb-1">
                    <p className="text-xs font-semibold uppercase tracking-widest text-neutral-500">Lead Details</p>
                    <button onClick={() => setEditing(true)} className="text-[11px] font-semibold text-[#002FA7] hover:underline" data-testid="edit-lead-details-btn">Edit</button>
                </div>
                {[
                    { icon: Mail, label: 'Email', value: lead.email },
                    { icon: Phone, label: 'Phone', value: lead.phone },
                    { icon: Globe, label: 'Website', value: lead.website, link: true },
                    { icon: Star, label: 'Rating', value: lead.rating ? `${lead.rating}/5 (${lead.review_count} reviews)` : null },
                    { icon: Tag, label: 'Category', value: lead.category },
                    { icon: Tag, label: 'Location', value: lead.location },
                ].map(({ icon: Icon, label, value, link }) => (
                    <div key={label} className="flex items-start gap-2 text-sm">
                        <Icon size={12} className="text-neutral-400 mt-0.5 flex-shrink-0" />
                        <div className="min-w-0">
                            <span className="text-xs text-neutral-400">{label}: </span>
                            {link && value ? (
                                <a href={value} target="_blank" rel="noopener noreferrer" className="text-[#002FA7] hover:underline text-xs break-all">{value}</a>
                            ) : (
                                <span className="text-xs text-neutral-700">{value || '—'}</span>
                            )}
                        </div>
                    </div>
                ))}
                <div className="flex items-center gap-2 pt-1">
                    <span className="text-xs text-neutral-400">Email Status:</span>
                    <EmailStatusBadge status={lead.email_status} />
                </div>

                {(lead.description || lead.solution_fit || lead.distance_mi != null) && (
                    <div className="border-t border-neutral-100 pt-2.5 mt-2.5 space-y-1.5">
                        {lead.solution_fit && <div className="text-xs"><span className="text-neutral-400">Solution fit: </span><span className="font-semibold text-neutral-700">{lead.solution_fit}</span></div>}
                        {lead.distance_mi != null && <div className="text-xs"><span className="text-neutral-400">Distance: </span><span className="text-neutral-700">{lead.distance_mi} mi</span></div>}
                        {lead.description && <p className="text-xs text-neutral-600 leading-relaxed">{lead.description}</p>}
                    </div>
                )}

                {(lead.company_email || lead.company_phone || lead.company_linkedin) && (
                    <div className="border-t border-neutral-100 pt-2.5 mt-2.5 space-y-1">
                        <p className="text-[10px] font-semibold uppercase tracking-widest text-neutral-400">Company Contact</p>
                        {lead.company_email && <div className="text-xs text-neutral-600">{lead.company_email}</div>}
                        {lead.company_phone && <div className="text-xs text-neutral-600">{lead.company_phone}</div>}
                        {lead.company_linkedin && <a href={lead.company_linkedin} target="_blank" rel="noopener noreferrer" className="text-xs text-[#002FA7] hover:underline break-all block">{lead.company_linkedin}</a>}
                    </div>
                )}

                {lead.contacts?.length > 0 && (
                    <div className="border-t border-neutral-100 pt-2.5 mt-2.5 space-y-2">
                        <p className="text-[10px] font-semibold uppercase tracking-widest text-neutral-400">Contacts ({lead.contacts.length})</p>
                        {lead.contacts.map((c, i) => (
                            <div key={i} className="text-xs bg-neutral-50 border border-neutral-100 p-2" data-testid={`contact-view-${i}`}>
                                <p className="font-semibold text-neutral-800">{c.name}{c.title ? ` — ${c.title}` : ''}</p>
                                {c.email && <p className="text-neutral-500">{c.email}</p>}
                                {c.phone && <p className="text-neutral-500">{c.phone}</p>}
                                {c.linkedin && <a href={c.linkedin} target="_blank" rel="noopener noreferrer" className="text-[#002FA7] hover:underline break-all block">{c.linkedin}</a>}
                            </div>
                        ))}
                    </div>
                )}

                {lead.sources?.length > 0 && (
                    <div className="border-t border-neutral-100 pt-2.5 mt-2.5 space-y-1">
                        <p className="text-[10px] font-semibold uppercase tracking-widest text-neutral-400">Sources</p>
                        {lead.sources.map((s, i) => (
                            <a key={i} href={s} target="_blank" rel="noopener noreferrer" className="text-xs text-[#002FA7] hover:underline break-all block">{s}</a>
                        ))}
                    </div>
                )}

                {lead.verification_notes && (
                    <div className="border-t border-neutral-100 pt-2.5 mt-2.5">
                        <p className="text-[10px] font-semibold uppercase tracking-widest text-neutral-400 mb-1">Verification Notes</p>
                        <p className="text-xs text-neutral-500 leading-relaxed italic">{lead.verification_notes}</p>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="border border-[#002FA7]/30 bg-[#002FA7]/[0.02] p-4 space-y-3" data-testid="lead-details-edit">
            <div className="flex items-center justify-between mb-1">
                <p className="text-xs font-semibold uppercase tracking-widest text-[#002FA7]">Editing Lead Details</p>
                <div className="flex items-center gap-2">
                    <button onClick={() => { setForm(toForm(lead)); setEditing(false); }} className="text-[11px] text-neutral-500 hover:underline" data-testid="cancel-edit-lead-btn">Cancel</button>
                    <button onClick={handleSave} disabled={saving} className="bg-[#002FA7] text-white text-[11px] font-semibold uppercase px-3 py-1 hover:bg-[#00227A] transition-colors disabled:opacity-50" data-testid="save-lead-details-btn">
                        {saving ? 'Saving…' : 'Save'}
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
                <div><label className={labelCls}>Name</label><input className={fieldCls} value={form.name} onChange={e => set('name', e.target.value)} data-testid="edit-name" /></div>
                <div><label className={labelCls}>Category</label><input className={fieldCls} value={form.category} onChange={e => set('category', e.target.value)} data-testid="edit-category" /></div>
            </div>
            <div><label className={labelCls}>Address</label><input className={fieldCls} value={form.address} onChange={e => set('address', e.target.value)} data-testid="edit-address" /></div>
            <div className="grid grid-cols-2 gap-3">
                <div><label className={labelCls}>Email</label><input className={fieldCls} value={form.email} onChange={e => set('email', e.target.value)} data-testid="edit-email" /></div>
                <div><label className={labelCls}>Phone</label><input className={fieldCls} value={form.phone} onChange={e => set('phone', e.target.value)} data-testid="edit-phone" /></div>
            </div>
            <div><label className={labelCls}>Website</label><input className={fieldCls} value={form.website} onChange={e => set('website', e.target.value)} data-testid="edit-website" /></div>
            <div className="grid grid-cols-2 gap-3">
                <div><label className={labelCls}>Location</label><input className={fieldCls} value={form.location} onChange={e => set('location', e.target.value)} data-testid="edit-location" /></div>
                <div><label className={labelCls}>Distance (mi)</label><input type="number" step="0.1" className={fieldCls} value={form.distance_mi} onChange={e => set('distance_mi', e.target.value)} data-testid="edit-distance" /></div>
            </div>
            <div><label className={labelCls}>Solution Fit</label><input className={fieldCls} value={form.solution_fit} onChange={e => set('solution_fit', e.target.value)} placeholder="e.g. VERY HIGH, HIGH, LOW" data-testid="edit-solution-fit" /></div>
            <div><label className={labelCls}>Description</label><textarea rows={2} className={`${fieldCls} resize-none leading-relaxed`} value={form.description} onChange={e => set('description', e.target.value)} data-testid="edit-description" /></div>

            <div className="grid grid-cols-2 gap-3 border-t border-[#002FA7]/10 pt-3">
                <div><label className={labelCls}>Company Email</label><input className={fieldCls} value={form.company_email} onChange={e => set('company_email', e.target.value)} data-testid="edit-company-email" /></div>
                <div><label className={labelCls}>Company Phone</label><input className={fieldCls} value={form.company_phone} onChange={e => set('company_phone', e.target.value)} data-testid="edit-company-phone" /></div>
            </div>
            <div><label className={labelCls}>Company LinkedIn</label><input className={fieldCls} value={form.company_linkedin} onChange={e => set('company_linkedin', e.target.value)} data-testid="edit-company-linkedin" /></div>

            <div className="border-t border-[#002FA7]/10 pt-3">
                <div className="flex items-center justify-between mb-1.5">
                    <label className={labelCls} style={{ marginBottom: 0 }}>Contacts</label>
                    <button type="button" onClick={addContact} className="text-[11px] font-semibold text-[#002FA7] hover:underline" data-testid="add-contact-btn">+ Add contact</button>
                </div>
                <div className="space-y-2">
                    {form.contacts.map((c, i) => (
                        <div key={i} className="border border-neutral-200 p-2.5 space-y-1.5" data-testid={`contact-edit-${i}`}>
                            <div className="flex items-center gap-1.5">
                                <input className={fieldCls} placeholder="Name" value={c.name} onChange={e => setContact(i, 'name', e.target.value)} data-testid={`contact-name-${i}`} />
                                <input className={fieldCls} placeholder="Title" value={c.title} onChange={e => setContact(i, 'title', e.target.value)} data-testid={`contact-title-${i}`} />
                                <button type="button" onClick={() => removeContact(i)} className="text-neutral-400 hover:text-red-600 flex-shrink-0" data-testid={`remove-contact-${i}`}><X size={13} /></button>
                            </div>
                            <div className="flex items-center gap-1.5">
                                <input className={fieldCls} placeholder="Email" value={c.email} onChange={e => setContact(i, 'email', e.target.value)} data-testid={`contact-email-${i}`} />
                                <input className={fieldCls} placeholder="Phone" value={c.phone} onChange={e => setContact(i, 'phone', e.target.value)} data-testid={`contact-phone-${i}`} />
                            </div>
                            <input className={fieldCls} placeholder="LinkedIn URL" value={c.linkedin} onChange={e => setContact(i, 'linkedin', e.target.value)} data-testid={`contact-linkedin-${i}`} />
                        </div>
                    ))}
                    {form.contacts.length === 0 && <p className="text-[11px] text-neutral-400 italic">No contacts yet.</p>}
                </div>
            </div>

            <div className="border-t border-[#002FA7]/10 pt-3">
                <div className="flex items-center justify-between mb-1.5">
                    <label className={labelCls} style={{ marginBottom: 0 }}>Sources</label>
                    <button type="button" onClick={addSource} className="text-[11px] font-semibold text-[#002FA7] hover:underline" data-testid="add-source-btn">+ Add source</button>
                </div>
                <div className="space-y-1.5">
                    {form.sources.map((s, i) => (
                        <div key={i} className="flex items-center gap-1.5">
                            <input className={fieldCls} placeholder="https://…" value={s} onChange={e => setSource(i, e.target.value)} data-testid={`source-input-${i}`} />
                            <button type="button" onClick={() => removeSource(i)} className="text-neutral-400 hover:text-red-600 flex-shrink-0" data-testid={`remove-source-${i}`}><X size={13} /></button>
                        </div>
                    ))}
                    {form.sources.length === 0 && <p className="text-[11px] text-neutral-400 italic">No sources yet.</p>}
                </div>
            </div>

            <div>
                <label className={labelCls}>Verification Notes</label>
                <textarea rows={2} className={`${fieldCls} resize-none leading-relaxed`} value={form.verification_notes} onChange={e => set('verification_notes', e.target.value)} data-testid="edit-verification-notes" />
            </div>
        </div>
    );
}

function LeadDetailDrawer({ lead, onClose, onUpdate }) {
    const navigate = useNavigate();
    const [note, setNote] = useState('');
    const [addingNote, setAddingNote] = useState(false);
    const [insights, setInsights] = useState(lead?.ai_insights || '');
    const [loadingInsights, setLoadingInsights] = useState(false);
    const [stage, setStage] = useState(lead?.pipeline_stage || 'new_leads');

    const handleAddNote = async () => {
        if (!note.trim()) return;
        setAddingNote(true);
        try {
            const { data } = await api.post(`/leadgen/leads/${lead.id}/note`, { note });
            onUpdate(data);
            setNote('');
        } catch { toast.error('Failed to save note'); } finally { setAddingNote(false); }
    };

    const handleLoadInsights = async () => {
        setLoadingInsights(true);
        try {
            const { data } = await api.post(`/leadgen/ai/insights/${lead.id}`, {});
            setInsights(data.insights);
        } catch { toast.error('Failed to generate insights'); } finally { setLoadingInsights(false); }
    };

    const handleStageChange = async (newStage) => {
        const prevStage = stage;
        setStage(newStage);
        try {
            const { data } = await api.put(`/leadgen/leads/${lead.id}/stage`, { stage: newStage });
            if (data?.client_created && data?.client_id) {
                toast.success('Lead won! Client created in BMHQ CRM.', {
                    action: {
                        label: 'View client',
                        onClick: () => navigate(`/clients/${data.client_id}`),
                    },
                    duration: 8000,
                });
            }
            onUpdate(data);
        } catch { setStage(prevStage); toast.error('Failed to update stage'); }
    };

    return (
        <div className="fixed inset-0 z-40 flex justify-end" data-testid="lead-drawer">
            <div className="absolute inset-0 bg-black/30" onClick={onClose} />
            <div className="relative bg-white border-l border-neutral-200 w-full max-w-lg overflow-y-auto z-10">
                {/* Header */}
                <div className="sticky top-0 bg-white border-b border-neutral-100 px-5 py-4 flex items-start justify-between z-10">
                    <div>
                        <h2 className="font-black text-lg tracking-tight flex items-center gap-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                            {lead.name}
                            {lead.is_manual && (
                                <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 border border-emerald-300 bg-emerald-50 text-emerald-700 rounded-none" data-testid="drawer-manual-badge">Manual</span>
                            )}
                        </h2>
                        <p className="text-xs text-neutral-500">{lead.category} · {lead.location}</p>
                    </div>
                    <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600" data-testid="close-drawer-btn">
                        <X size={18} />
                    </button>
                </div>

                <div className="p-5 space-y-6">
                    {/* Score + Stage */}
                    <div className="flex items-center gap-3">
                        <div className="flex-1">
                            <p className="text-xs font-semibold uppercase tracking-widest text-neutral-500 mb-1">Lead Score</p>
                            <ScoreBadge score={lead.score ?? 0} />
                        </div>
                        <div className="flex-1">
                            <p className="text-xs font-semibold uppercase tracking-widest text-neutral-500 mb-1">Stage</p>
                            <select
                                value={stage}
                                onChange={e => handleStageChange(e.target.value)}
                                className="text-xs border border-neutral-200 bg-white px-2 py-1 rounded-none focus:outline-none focus:border-[#002FA7]"
                                data-testid="stage-select"
                            >
                                {STAGES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                            </select>
                        </div>
                    </div>

                    {/* Discovery Call */}
                    <DiscoveryCallSection lead={lead} onUpdate={onUpdate} />

                    {/* Lead Details — editable */}
                    <LeadDetailsEditor lead={lead} onUpdate={onUpdate} />

                    {/* Triggers */}
                    {lead.triggers?.length > 0 && (
                        <div>
                            <p className="text-xs font-semibold uppercase tracking-widest text-neutral-500 mb-2">Detected Triggers</p>
                            <div className="flex flex-wrap gap-1.5">
                                {lead.triggers.map((t) => <TriggerBadge key={t.type} trigger={t} />)}
                            </div>
                            <div className="mt-3 space-y-1.5">
                                {lead.triggers.map((t, i) => (
                                    <div key={t.type || i} className="flex items-center gap-2 text-xs text-neutral-500">
                                        <ArrowRight size={10} className="text-[#002FA7]" />
                                        <span>Opportunity: <strong>{t.opportunity}</strong></span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* AI Insights */}
                    <div className="border border-neutral-100 p-4">
                        <div className="flex items-center justify-between mb-2">
                            <p className="text-xs font-semibold uppercase tracking-widest text-neutral-500">AI Insights</p>
                            <button
                                onClick={handleLoadInsights}
                                disabled={loadingInsights}
                                className="flex items-center gap-1 text-xs text-[#002FA7] hover:underline font-semibold disabled:opacity-50"
                                data-testid="generate-insights-btn"
                            >
                                <Zap size={10} />
                                {loadingInsights ? 'Generating...' : 'Generate'}
                            </button>
                        </div>
                        {insights ? (
                            <p className="text-sm text-neutral-600 leading-relaxed">{insights}</p>
                        ) : (
                            <p className="text-xs text-neutral-400 italic">Click "Generate" for AI-powered lead analysis.</p>
                        )}
                    </div>

                    {/* Notes */}
                    <div>
                        <p className="text-xs font-semibold uppercase tracking-widest text-neutral-500 mb-2">Notes</p>
                        <div className="space-y-2 mb-3">
                            {(lead.notes || []).map((n, i) => (
                                <div key={typeof n === 'object' ? n.created_at : i} className="border border-neutral-100 bg-neutral-50 p-3 text-xs text-neutral-700">
                                    {typeof n === 'object' ? n.text : n}
                                    {typeof n === 'object' && <p className="text-neutral-400 mt-1">{n.created_at?.substring(0, 10)}</p>}
                                </div>
                            ))}
                        </div>
                        <div className="flex gap-2">
                            <input
                                type="text"
                                value={note}
                                onChange={e => setNote(e.target.value)}
                                placeholder="Add a note..."
                                className="flex-1 border border-neutral-200 bg-white px-3 py-2 text-xs rounded-none focus:outline-none focus:border-[#002FA7]"
                                onKeyDown={e => e.key === 'Enter' && handleAddNote()}
                                data-testid="note-input"
                            />
                            <button
                                onClick={handleAddNote}
                                disabled={addingNote}
                                className="bg-[#002FA7] text-white px-3 py-2 text-xs font-semibold uppercase tracking-wider hover:bg-[#00227A] transition-colors"
                                data-testid="add-note-btn"
                            >
                                Add
                            </button>
                        </div>
                    </div>

                    {/* Activity */}
                    {lead.activity?.length > 0 && (
                        <div>
                            <p className="text-xs font-semibold uppercase tracking-widest text-neutral-500 mb-2">Activity</p>
                            <div className="space-y-2">
                                {[...lead.activity].reverse().map((a, i) => (
                                    <div key={a.created_at || i} className="flex items-start gap-2 text-xs text-neutral-500">
                                        <Activity size={10} className="mt-0.5 text-neutral-400 flex-shrink-0" />
                                        <div>
                                            <span>{a.description}</span>
                                            <span className="text-neutral-300 ml-2">{a.created_at?.substring(0, 10)}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default function LeadsDatabase() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [leads, setLeads] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState([]);
    const [activeLead, setActiveLead] = useState(null);
    const [page, setPage] = useState(1);
    const [filters, setFilters] = useState({
        stage: '',
        email_status: '',
        has_email: '',
        has_website: '',
        emailed: '',
        is_manual: '',
        search: '',
        source: 'all',
        follow_up: searchParams.get('follow_up') || '',  // initialise from URL
    });
    const [sortBy, setSortBy] = useState('created_at');
    const [sortDir, setSortDir] = useState('desc');
    const [verifyModal, setVerifyModal] = useState(false);
    const [verifying, setVerifying] = useState(false);
    const [verifyCostEstimate, setVerifyCostEstimate] = useState(null);
    const [verifyResults, setVerifyResults] = useState(null);

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    const loadLeads = useCallback(async () => {
        setLoading(true);
        try {
            const params = { limit: PAGE_SIZE, skip: (page - 1) * PAGE_SIZE, sort_by: sortBy, sort_dir: sortDir, source: filters.source || 'all' };
            if (filters.stage) params.stage = filters.stage;
            if (filters.email_status) params.email_status = filters.email_status;
            if (filters.has_email === 'true') params.has_email = true;
            if (filters.has_email === 'false') params.has_email = false;
            if (filters.has_website === 'true') params.has_website = true;
            if (filters.has_website === 'false') params.has_website = false;
            if (filters.emailed === 'true') params.emailed = true;
            if (filters.emailed === 'false') params.emailed = false;
            if (filters.is_manual === 'true') params.is_manual = true;
            if (filters.is_manual === 'false') params.is_manual = false;
            if (filters.search) params.search = filters.search;
            if (filters.follow_up) params.follow_up = filters.follow_up;
            const { data } = await api.get(`/leadgen/leads`, { params });
            setLeads(data.leads || []);
            setTotal(data.total || 0);
        } catch { toast.error('Failed to load leads'); } finally { setLoading(false); }
    }, [filters, page, sortBy, sortDir]);

    useEffect(() => { loadLeads(); }, [loadLeads]);

    // Reset to page 1 when filters change
    const updateFilter = (key, value) => {
        setPage(1);
        setFilters(p => ({ ...p, [key]: value }));
        setSelected([]);
        // Keep follow_up reflected in the URL so the tile deep-link is shareable
        if (key === 'follow_up') {
            const sp = new URLSearchParams(searchParams);
            if (value) sp.set('follow_up', value);
            else sp.delete('follow_up');
            setSearchParams(sp, { replace: true });
        }
    };

    // Click a sortable header: same field → flip dir; new field → asc by default
    const toggleSort = (field) => {
        setPage(1);
        setSelected([]);
        if (sortBy === field) {
            setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortBy(field);
            setSortDir('asc');
        }
    };

    const toggleSelect = (id) => setSelected(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
    const selectableLeads = leads.filter(l => l.source !== 'prospect_import');
    const toggleAll = () => setSelected(selected.length === selectableLeads.length ? [] : selectableLeads.map(l => l.id));

    const handleBulkDelete = async () => {
        if (!window.confirm(`Delete ${selected.length} leads?`)) return;
        await api.post(`/leadgen/leads/bulk-delete`, { lead_ids: selected });
        setSelected([]);
        loadLeads();
    };

    const handleVerifyEstimate = async () => {
        const ids = selected.length ? selected : leads.filter(l => l.email).map(l => l.id);
        if (!ids.length) return;
        const { data } = await api.post(`/leadgen/verify/estimate`, { lead_ids: ids });
        setVerifyCostEstimate({ ...data, lead_ids: ids });
        setVerifyModal(true);
    };

    const handleVerifyRun = async () => {
        setVerifying(true);
        try {
            const { data } = await api.post(`/leadgen/verify/run`, { lead_ids: verifyCostEstimate.lead_ids });
            // Tally results by status
            const tally = { valid: 0, risky: 0, invalid: 0, no_email: 0 };
            (data.results || []).forEach(r => {
                const s = r.status || 'no_email';
                tally[s] = (tally[s] || 0) + 1;
            });
            setVerifyResults({ ...data, tally });
            await loadLeads();
        } catch (err) {
            alert(err.response?.data?.detail || 'Verification failed');
        } finally {
            setVerifying(false);
            setVerifyModal(false);
        }
    };

    const handleBulkStage = async (stage) => {
        if (!selected.length) return;
        await api.post(`/leadgen/leads/bulk-stage`, { lead_ids: selected, stage });
        setSelected([]);
        loadLeads();
    };

    return (
        <div className="flex flex-col h-full" data-testid="leads-database-page">
            {/* Header */}
            <div className="border-b border-neutral-200 bg-white px-6 py-4">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h1 className="text-2xl font-black tracking-tight text-neutral-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Leads Database</h1>
                        <p className="text-xs text-neutral-500 mt-0.5">{total} total leads</p>
                    </div>
                    <div className="flex items-center gap-2">
                        {selected.length > 0 && (
                            <div className="flex items-center gap-2 border border-neutral-200 bg-neutral-50 px-3 py-1.5" data-testid="bulk-actions-bar">
                                <span className="text-xs font-semibold text-neutral-600">{selected.length} selected</span>
                                <select
                                    onChange={e => { if (e.target.value) handleBulkStage(e.target.value); e.target.value = ''; }}
                                    className="text-xs border border-neutral-200 bg-white px-2 py-1 rounded-none"
                                    defaultValue=""
                                    data-testid="bulk-stage-select"
                                >
                                    <option value="">Move to stage...</option>
                                    {STAGES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                                </select>
                                <button
                                    onClick={handleVerifyEstimate}
                                    className="flex items-center gap-1 text-xs bg-[#002FA7] text-white px-3 py-1.5 uppercase tracking-wider font-semibold hover:bg-[#00227A] transition-colors"
                                    data-testid="verify-selected-btn"
                                >
                                    <CheckCircle size={10} />
                                    Verify Emails
                                </button>
                                <button
                                    onClick={handleBulkDelete}
                                    className="flex items-center gap-1 text-xs bg-red-50 text-red-600 border border-red-200 px-2 py-1.5 hover:bg-red-100 transition-colors"
                                    data-testid="bulk-delete-btn"
                                >
                                    <Trash2 size={10} />
                                </button>
                            </div>
                        )}
                        <button
                            onClick={handleVerifyEstimate}
                            className="flex items-center gap-1.5 text-xs border border-[#002FA7] text-[#002FA7] px-3 py-2 font-semibold uppercase tracking-wider hover:bg-[#002FA7] hover:text-white transition-colors"
                            data-testid="verify-all-btn"
                        >
                            <CheckCircle size={11} />
                            Verify All Emails
                        </button>
                    </div>
                </div>

                {/* Filters */}
                <div className="flex items-center gap-3 flex-wrap">
                    <div className="relative">
                        <Search size={11} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-400" />
                        <input
                            type="text"
                            placeholder="Search leads..."
                            value={filters.search}
                            onChange={e => updateFilter('search', e.target.value)}
                            className="border border-neutral-200 bg-neutral-50 pl-7 pr-3 py-1.5 text-xs rounded-none focus:outline-none focus:border-[#002FA7] w-48"
                            data-testid="leads-search-input"
                        />
                    </div>
                    <select
                        value={filters.stage}
                        onChange={e => updateFilter('stage', e.target.value)}
                        className="text-xs border border-neutral-200 bg-neutral-50 px-2 py-1.5 rounded-none focus:outline-none focus:border-[#002FA7]"
                        data-testid="stage-filter"
                    >
                        <option value="">All Stages</option>
                        {STAGES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                    </select>
                    <select
                        value={filters.email_status}
                        onChange={e => updateFilter('email_status', e.target.value)}
                        className="text-xs border border-neutral-200 bg-neutral-50 px-2 py-1.5 rounded-none focus:outline-none"
                        data-testid="email-status-filter"
                    >
                        <option value="">All Email Status</option>
                        <option value="valid">Valid</option>
                        <option value="risky">Risky</option>
                        <option value="invalid">Invalid</option>
                        <option value="not_verified">Unverified</option>
                    </select>
                    <select
                        value={filters.has_email}
                        onChange={e => updateFilter('has_email', e.target.value)}
                        className="text-xs border border-neutral-200 bg-neutral-50 px-2 py-1.5 rounded-none focus:outline-none"
                        data-testid="has-email-filter"
                    >
                        <option value="">Has Email?</option>
                        <option value="true">Has Email</option>
                        <option value="false">No Email</option>
                    </select>
                    <select
                        value={filters.has_website}
                        onChange={e => updateFilter('has_website', e.target.value)}
                        className="text-xs border border-neutral-200 bg-neutral-50 px-2 py-1.5 rounded-none focus:outline-none"
                        data-testid="has-website-filter"
                    >
                        <option value="">Has Website?</option>
                        <option value="true">Has Website</option>
                        <option value="false">No Website</option>
                    </select>
                    <select
                        value={filters.emailed}
                        onChange={e => updateFilter('emailed', e.target.value)}
                        className="text-xs border border-neutral-200 bg-neutral-50 px-2 py-1.5 rounded-none focus:outline-none"
                        data-testid="emailed-filter"
                    >
                        <option value="">Emailed Before?</option>
                        <option value="true">Already Emailed</option>
                        <option value="false">Never Emailed</option>
                    </select>
                    <select
                        value={filters.follow_up}
                        onChange={e => updateFilter('follow_up', e.target.value)}
                        className="text-xs border border-neutral-200 bg-neutral-50 px-2 py-1.5 rounded-none focus:outline-none"
                        data-testid="follow-up-filter"
                    >
                        <option value="">All Follow-ups</option>
                        <option value="due">Due (today + overdue)</option>
                        <option value="overdue">Overdue only</option>
                        <option value="today">Due today</option>
                        <option value="upcoming">Upcoming (7 days)</option>
                    </select>
                    <select
                        value={filters.is_manual}
                        onChange={e => updateFilter('is_manual', e.target.value)}
                        className="text-xs border border-neutral-200 bg-neutral-50 px-2 py-1.5 rounded-none focus:outline-none"
                        data-testid="manual-import-filter"
                    >
                        <option value="">All Sources</option>
                        <option value="true">Manual / Imported</option>
                        <option value="false">Scraped (Outscraper)</option>
                    </select>
                    <select
                        value={filters.source}
                        onChange={e => updateFilter('source', e.target.value)}
                        className="text-xs border border-neutral-200 bg-neutral-50 px-2 py-1.5 rounded-none focus:outline-none"
                        data-testid="prospect-source-filter"
                    >
                        <option value="all">Scraped + Imported Prospects</option>
                        <option value="scraped">Scraped only</option>
                        <option value="imported">Imported Prospects only</option>
                    </select>
                </div>
            </div>

            {/* Table */}
            <div className="flex-1 overflow-auto">

                {/* Verification result banner */}
                {verifyResults && (
                    <div className="border-b border-green-200 bg-green-50 px-6 py-3 flex items-center justify-between" data-testid="verify-result-banner">
                        <div className="flex items-center gap-5 flex-wrap">
                            <span className="text-xs font-bold uppercase tracking-wider text-green-800">
                                Verification complete — {verifyResults.count} checked
                            </span>
                            <div className="flex items-center gap-4">
                                {verifyResults.tally?.valid > 0 && (
                                    <span className="flex items-center gap-1.5 text-xs font-semibold text-green-700" data-testid="verify-valid-count">
                                        <CheckCircle size={13} />
                                        {verifyResults.tally.valid} valid
                                    </span>
                                )}
                                {verifyResults.tally?.risky > 0 && (
                                    <span className="flex items-center gap-1.5 text-xs font-semibold text-amber-600" data-testid="verify-risky-count">
                                        <AlertTriangle size={13} />
                                        {verifyResults.tally.risky} risky
                                    </span>
                                )}
                                {verifyResults.tally?.invalid > 0 && (
                                    <span className="flex items-center gap-1.5 text-xs font-semibold text-red-600" data-testid="verify-invalid-count">
                                        <XCircle size={13} />
                                        {verifyResults.tally.invalid} invalid
                                    </span>
                                )}
                                {verifyResults.tally?.no_email > 0 && (
                                    <span className="flex items-center gap-1.5 text-xs font-semibold text-neutral-400" data-testid="verify-noemail-count">
                                        — {verifyResults.tally.no_email} no email
                                    </span>
                                )}
                            </div>
                        </div>
                        <button
                            onClick={() => setVerifyResults(null)}
                            className="text-green-400 hover:text-green-700 transition-colors ml-4 flex-shrink-0"
                            data-testid="dismiss-verify-banner"
                        >
                            <X size={14} />
                        </button>
                    </div>
                )}
                {loading ? (
                    <div className="p-8 text-center text-sm text-neutral-400">Loading leads...</div>
                ) : !leads.length ? (
                    <div className="flex flex-col items-center justify-center h-64 text-center">
                        <p className="text-lg font-black text-neutral-300 mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>No leads found</p>
                        <p className="text-sm text-neutral-400">Try adjusting filters or discover new leads</p>
                    </div>
                ) : (
                    <table className="w-full table-dense" data-testid="leads-table">
                        <thead className="sticky top-0 z-10">
                            <tr>
                                <th
                                    className="w-10 px-4 py-3 bg-neutral-50 border-b border-neutral-200 cursor-pointer"
                                    onClick={toggleAll}
                                    data-testid="select-all-checkbox"
                                >
                                    <input
                                        type="checkbox"
                                        checked={selected.length === selectableLeads.length && selectableLeads.length > 0}
                                        onChange={() => {}}
                                        className="accent-[#002FA7] pointer-events-none"
                                    />
                                </th>
                                <SortableTh field="name" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort}>Business</SortableTh>
                                <SortableTh field="category" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort}>Category</SortableTh>
                                <SortableTh field="email" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort}>Email</SortableTh>
                                <SortableTh field="email_status" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} align="center">Email Status</SortableTh>
                                <SortableTh field="rating" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} align="center">Rating</SortableTh>
                                <SortableTh field="score" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} align="center">Score</SortableTh>
                                <SortableTh field="pipeline_stage" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort}>Stage</SortableTh>
                                <th className="text-left">Triggers</th>
                                <SortableTh field="created_at" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} align="center">Added</SortableTh>
                            </tr>
                        </thead>
                        <tbody>
                            {leads.map((lead) => {
                                const isProspect = lead.source === 'prospect_import';
                                return (
                                <tr
                                    key={lead.id}
                                    className={`hover:bg-neutral-50 transition-colors cursor-pointer ${selected.includes(lead.id) ? 'bg-blue-50' : ''}`}
                                    onClick={() => isProspect ? navigate(`/clients/${lead.client_id}`) : setActiveLead(lead)}
                                    data-testid={`lead-row-${lead.id}`}
                                >
                                    <td
                                        className="px-4 cursor-pointer"
                                        onClick={e => { e.stopPropagation(); if (!isProspect) toggleSelect(lead.id); }}
                                        data-testid={`checkbox-cell-${lead.id}`}
                                    >
                                        {!isProspect && (
                                            <input
                                                type="checkbox"
                                                checked={selected.includes(lead.id)}
                                                onChange={() => {}}
                                                className="accent-[#002FA7] pointer-events-none"
                                            />
                                        )}
                                    </td>
                                    <td>
                                        <p className="font-semibold text-neutral-900 text-sm flex items-center gap-1.5">
                                            {lead.name}
                                            {lead.is_manual && (
                                                <span className="text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 border border-emerald-300 bg-emerald-50 text-emerald-700 rounded-none flex-shrink-0" data-testid={`manual-badge-${lead.id}`}>
                                                    Manual
                                                </span>
                                            )}
                                            {isProspect && (
                                                <span className="text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 border border-teal-300 bg-teal-50 text-teal-700 rounded-none flex-shrink-0" data-testid={`imported-badge-${lead.id}`}>
                                                    Imported
                                                </span>
                                            )}
                                        </p>
                                        <p className="text-xs text-neutral-400 truncate max-w-[180px]">{lead.address}</p>
                                    </td>
                                    <td><span className="text-xs text-neutral-600">{lead.category || '—'}</span></td>
                                    <td>
                                        {lead.email
                                            ? <span className="text-xs text-neutral-600 truncate max-w-[160px] block">{lead.email}</span>
                                            : <span className="text-neutral-300 text-xs">—</span>}
                                    </td>
                                    <td className="text-center"><EmailStatusBadge status={lead.email_status} /></td>
                                    <td className="text-center">
                                        {lead.rating
                                            ? <span className="flex items-center justify-center gap-1 text-xs text-amber-600"><Star size={10} fill="currentColor" />{lead.rating}</span>
                                            : '—'}
                                    </td>
                                    <td className="text-center">{isProspect ? <span className="text-neutral-300 text-xs">—</span> : <ScoreBadge score={lead.score ?? 0} />}</td>
                                    <td><StageBadge stage={isProspect ? lead.client_status : lead.pipeline_stage} /></td>
                                    <td>
                                        <div className="flex flex-wrap gap-1">
                                            {(lead.triggers || []).slice(0, 2).map((t) => (
                                                <TriggerBadge key={t.type} trigger={t} />
                                            ))}
                                        </div>
                                    </td>
                                    <td className="text-center">
                                        <span className="text-[10px] text-neutral-400 whitespace-nowrap">
                                            {lead.created_at ? new Date(lead.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }) : '—'}
                                        </span>
                                    </td>
                                </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Pagination */}
            {!loading && total > PAGE_SIZE && (
                <div className="border-t border-neutral-200 bg-white px-6 py-3 flex items-center justify-between flex-shrink-0" data-testid="pagination-bar">
                    <span className="text-xs text-neutral-500">
                        Showing {((page - 1) * PAGE_SIZE) + 1}–{Math.min(page * PAGE_SIZE, total)} of {total} leads
                    </span>
                    <div className="flex items-center gap-1">
                        <button
                            onClick={() => setPage(p => Math.max(1, p - 1))}
                            disabled={page === 1}
                            className="p-1.5 border border-neutral-200 text-neutral-600 hover:bg-neutral-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                            data-testid="prev-page-btn"
                        >
                            <ChevronLeft size={13} />
                        </button>
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            let pageNum;
                            if (totalPages <= 5) {
                                pageNum = i + 1;
                            } else if (page <= 3) {
                                pageNum = i + 1;
                            } else if (page >= totalPages - 2) {
                                pageNum = totalPages - 4 + i;
                            } else {
                                pageNum = page - 2 + i;
                            }
                            return (
                                <button
                                    key={pageNum}
                                    onClick={() => setPage(pageNum)}
                                    className={`w-7 h-7 text-xs font-semibold border transition-colors ${page === pageNum ? 'bg-[#002FA7] text-white border-[#002FA7]' : 'border-neutral-200 text-neutral-600 hover:bg-neutral-50'}`}
                                    data-testid={`page-btn-${pageNum}`}
                                >
                                    {pageNum}
                                </button>
                            );
                        })}
                        <button
                            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                            disabled={page === totalPages}
                            className="p-1.5 border border-neutral-200 text-neutral-600 hover:bg-neutral-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                            data-testid="next-page-btn"
                        >
                            <ChevronRight size={13} />
                        </button>
                    </div>
                </div>
            )}

            {/* Verify Modal */}
            {verifyModal && verifyCostEstimate && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
                    <div className="bg-white border border-neutral-200 w-full max-w-md" data-testid="verify-cost-modal">
                        <div className="p-6 border-b border-neutral-100">
                            <h2 className="text-xl font-black tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Email Verification</h2>
                        </div>
                        <div className="p-6 space-y-4">
                            <div className="bg-neutral-50 border border-neutral-200 p-4 space-y-3">
                                <div className="flex justify-between text-sm">
                                    <span className="text-neutral-600">Emails to verify</span>
                                    <span className="font-semibold">{verifyCostEstimate.emails}</span>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span className="text-neutral-600">Cost per 1,000</span>
                                    <span className="font-semibold">${verifyCostEstimate.cost_per_1000}</span>
                                </div>
                                <div className="border-t border-neutral-200 pt-3 flex justify-between">
                                    <span className="font-semibold uppercase tracking-wider text-xs">Estimated Cost</span>
                                    <span className="text-lg font-black text-[#002FA7]">${verifyCostEstimate.estimated_cost?.toFixed(4)}</span>
                                </div>
                            </div>
                            <p className="text-xs text-neutral-400">{verifyCostEstimate.note}</p>
                        </div>
                        <div className="p-6 pt-0 flex gap-3">
                            <button onClick={() => setVerifyModal(false)} className="flex-1 border border-neutral-200 text-neutral-700 font-semibold uppercase text-xs py-2.5 hover:bg-neutral-50 transition-colors">
                                Cancel
                            </button>
                            <button
                                onClick={handleVerifyRun}
                                disabled={verifying}
                                className="flex-1 bg-[#002FA7] hover:bg-[#00227A] text-white font-semibold uppercase text-xs py-2.5 transition-colors disabled:opacity-50"
                                data-testid="confirm-verify-btn"
                            >
                                {verifying ? 'Verifying...' : 'Verify Now'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {activeLead && (
                <LeadDetailDrawer
                    lead={activeLead}
                    onClose={() => setActiveLead(null)}
                    onUpdate={(updated) => {
                        setLeads(p => p.map(l => l.id === updated.id ? updated : l));
                        setActiveLead(updated);
                    }}
                />
            )}
        </div>
    );
}
